import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useCandidateProfile } from './useCandidateProfile';

interface CompletionItem {
  label: string;
  done: boolean;
  sectionId: string;
  editId?: string;
}

export function useProfileCompletion() {
  const { t } = useTranslation();
  const {
    profile, profileLoading, experiences, education, skills, languages,
  } = useCandidateProfile();

  const { completionScore, missingItems, totalItems } = useMemo(() => {
    if (!profile) return { completionScore: 0, missingItems: [] as CompletionItem[], totalItems: 0 };

    const items: CompletionItem[] = [
      // Personal details
      { label: t('seek.completion.currentJobTitle'), done: !!profile.current_job_title, sectionId: 'section-personal' },
      { label: t('seek.completion.professionalSummary'), done: !!profile.professional_summary, sectionId: 'section-personal' },
      { label: t('seek.completion.phone'), done: !!profile.phone, sectionId: 'section-personal' },
      { label: t('seek.completion.country'), done: !!profile.country, sectionId: 'section-personal' },
      { label: t('seek.completion.nationality'), done: !!profile.nationality, sectionId: 'section-personal' },
      { label: t('seek.completion.profilePhoto'), done: !!profile.avatar_url, sectionId: 'section-personal', editId: 'avatar' },
      // Sections
      { label: skills.length >= 5 ? t('seek.completion.skillsDone') : t('seek.completion.skillsProgress', { count: skills.length }), done: skills.length >= 5, sectionId: 'section-skills' },
      { label: t('seek.completion.atLeastOneExperience'), done: experiences.length > 0 || !!profile.is_fresh_graduate, sectionId: 'section-experience' },
      { label: t('seek.completion.atLeastOneEducation'), done: education.length > 0, sectionId: 'section-education' },
      { label: t('seek.completion.atLeastOneLanguage'), done: languages.length > 0, sectionId: 'section-languages' },
    ];

    // Experience detail checks (skip if fresh graduate)
    if (!profile.is_fresh_graduate && experiences.length > 0) {
      experiences.forEach((exp) => {
        items.push({ label: t('seek.completion.experienceStartDate'), done: !!exp.start_date, sectionId: 'section-experience', editId: exp.id });
        items.push({ label: t('seek.completion.experienceEndDate'), done: !!exp.end_date || !!exp.is_current, sectionId: 'section-experience', editId: exp.id });
        items.push({ label: t('seek.completion.experienceEmployer'), done: !!exp.employer_name, sectionId: 'section-experience', editId: exp.id });
        items.push({ label: t('seek.completion.experienceType'), done: !!exp.employment_type, sectionId: 'section-experience', editId: exp.id });
        items.push({ label: t('seek.completion.experienceLocation'), done: !!exp.location, sectionId: 'section-experience', editId: exp.id });
        items.push({ label: t('seek.completion.experienceDescription'), done: !!exp.description, sectionId: 'section-experience', editId: exp.id });
      });
    }

    // Education detail checks
    if (education.length > 0) {
      education.forEach((edu) => {
        items.push({ label: t('seek.completion.educationDegree'), done: !!edu.degree, sectionId: 'section-education', editId: edu.id });
        items.push({ label: t('seek.completion.educationFieldOfStudy'), done: !!edu.field_of_study, sectionId: 'section-education', editId: edu.id });
        items.push({ label: t('seek.completion.educationStartDate'), done: !!edu.start_date, sectionId: 'section-education', editId: edu.id });
        items.push({ label: t('seek.completion.educationEndDate'), done: !!edu.end_date, sectionId: 'section-education', editId: edu.id });
        items.push({ label: t('seek.completion.educationLocation'), done: !!edu.location, sectionId: 'section-education', editId: edu.id });
      });
    }

    const score = Math.round((items.filter(i => i.done).length / items.length) * 100);
    const missing = items.filter(i => !i.done);
    return { completionScore: score, missingItems: missing, totalItems: items.length };
  }, [profile, experiences, education, skills, languages, t]);

  return {
    completionScore,
    missingItems,
    totalItems,
    isComplete: completionScore === 100,
    isLoading: profileLoading,
  };
}
